import { differenceInSeconds } from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Component, Inject, OnInit, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';

import { SnAbilityService } from '@lib/ability';
import { SnLiveVideoUrls } from '@lib/api/entities';
import { TimelineService } from '@lib/api/services';
import { SnSessionService } from '@lib/auth';
import { ISnPlayer } from '@lib/player/common';
import { SnVideoPlayerComponent } from '@lib/player/video-player';
import { ITimeline, ITimelineDefinition } from '@lib/timeline';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';
import { SnVaultDialogComponent } from '@lib/vault';

@Component({
  selector: 'fs-clip-view-video-dialog',
  templateUrl: './clip-view-video-dialog.component.html',
  styleUrls: ['./clip-view-video-dialog.component.scss'],
})
export class ClipViewVideoDialogComponent implements OnInit, OnDestroy {
  get canDownload(): boolean {
    return this._ability.can({
      subject: 'incidents/recording',
      action: 'can_download',
    });
  }

  @ViewChild('videoPlayer')
  public player: SnVideoPlayerComponent;

  public src: SnLiveVideoUrls;
  public downloadStartDate: number;
  public downloadEndDate: number;
  public clipLoading: boolean;
  public isLoading: boolean = false;
  public isVideoLoading: boolean = false;
  public unavailable = false;
  public canClip: boolean = false;

  private _timelinePath: ITimeline;
  private _startDate: Date;
  private _endDate: Date;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _matDialog: MatDialog,
    private readonly _matDialogRef: MatDialogRef<ClipViewVideoDialogComponent>,
    private readonly _changeDetector: ChangeDetectorRef,
    private readonly _timelineService: TimelineService,
    private readonly _ability: SnAbilityService,
    private readonly _sessionService: SnSessionService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  public ngOnInit(): void {
    const timestamp = new Date(this.data.item.timestamp).getTime();
    const recording = this.data.camera.record && this.data.camera.playbackUrls;
    const loopStart = Date.now() / 1000 - this.data.camera.loopDuration;
    if (timestamp * 1000 < loopStart || !recording) {
      this.unavailable = true;
      return;
    }
    this._startDate = new Date(timestamp - 15 * 1000);
    this._endDate = new Date(timestamp + 15 * 1000);

    this.isVideoLoading = true;
    if (!this.data.camera.isFususCamera) {
      this.src =
        this.data.camera.isManaged &&
        !this.data.camera.isExternal &&
        !this._sessionService.isFederal
          ? new SnLiveVideoUrls({
              main: this.data.camera.playbackUrls.main.toStreamSourceListFmp4(
                null,
                this._startDate,
                this._endDate,
              ),
            })
          : new SnLiveVideoUrls({
              main: this.data.camera.playbackUrls.main.toStreamSourceList(
                null,
                this._startDate,
                this._endDate,
              ),
            });
      this.isVideoLoading = false;
    } else {
      const url = this.data.camera.playbackUrls?.main.local.management;
      this._timelineService
        .getTimelineData(url)
        .pipe(takeUntil(this._destroy$))
        .subscribe((res: ITimelineDefinition[]) => {
          this._timelinePath = this._timelineService.getTimelinePath(
            res[0].timeline,
            this._startDate.getTime(),
          );

          if (this._timelinePath) {
            this.src = new SnLiveVideoUrls({
              main: this.data.camera.playbackUrls.main.toFususStreamSourceList(
                this._timelinePath.file_path,
                this._startDate,
              ),
            });
            this._changeDetector.detectChanges();
          }

          if (!this._timelinePath) {
            this.unavailable = true;
            this._changeDetector.detectChanges();
            this.isVideoLoading = false;
            return;
          }

          this._changeDetector.detectChanges();
          this.isVideoLoading = false;
        });
    }

    this.downloadStartDate = Math.floor(this._startDate.getTime() / 1000);
    this.downloadEndDate = Math.ceil(this._endDate.getTime() / 1000);
    this._changeDetector.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public cancel(): void {
    this._matDialogRef.close();
  }

  public getProgress($event: boolean): void {
    this.clipLoading = $event;
    this._changeDetector.detectChanges();
  }

  public onPlayerInit(player: ISnPlayer): void {
    player.connected$.pipe(takeUntil(this._destroy$)).subscribe((connected) => {
      this.canClip = connected;
      if (this._timelinePath && connected) {
        const timedifference = this._determineTimeDifference(
          this._startDate,
          this._timelinePath.start,
        );
        this.player.forward(timedifference);
        this._changeDetector.detectChanges();
      }
    });
  }

  public clip(): void {
    if (!this.player) {
      return;
    }
    document.exitFullscreen();

    this.player.isClipping = true;
    const video = this.player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    this._matDialog
      .open(SnShareImageDialogComponent, {
        data: { url: canvas.toDataURL() },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.player.isClipping = false;
      });
  }

  public addToVault(): void {
    this.isLoading = true;
    const start = Math.floor(this._startDate.getTime() / 1000);
    const end = Math.ceil(this._endDate.getTime() / 1000);
    this._matDialog
      .open(SnVaultDialogComponent, {
        data: {
          cameraId: this.data.camera.id,
          start,
          end,
        },
      })
      .afterClosed()
      .subscribe({
        next: () => {
          this.isLoading = false;
          this._matDialogRef.close();
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  private _determineTimeDifference(eventDate: Date, timelineStart: number): number {
    const timelineDate = new Date(timelineStart * 1000);
    return differenceInSeconds(eventDate, timelineDate);
  }
}
